import { ref, computed, onBeforeMount, onUnmounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useBaseMenuStore } from '@/common/stores/base-menu';
import { MenuInfo } from '@/common/components/organisms/baseMenu/types';
import { MENU_HEIGHT } from '@/common/components/organisms/baseMenu/baseMenu.define';
import { hasNoChildren } from '@/common/components/organisms/baseMenu/baseMenu.uses';

export interface Props {
  menuInfo: MenuInfo;
  dashboardMenuList?: MenuInfo[];
  analysisBoardMenuList?: MenuInfo[];
  expanded?: boolean;
  selected?: boolean;
}

export interface Emit {
  (e: 'hover', evt: MouseEvent): void;
  (e: 'click'): void;
}

export const setup = (props: Props, emit: Emit) => {
  const { mainPanelInfo } = storeToRefs(useBaseMenuStore());

  const innerRef = ref();
  const innerHeight = ref<string>('0px');

  const hasMenuContent = computed(() => {
    return (
      (props.menuInfo.children && props.menuInfo.children.length) ||
      props.menuInfo.dashboardList ||
      props.menuInfo.analysisBoardList ||
      (props.menuInfo.subMenuList && props.menuInfo.subMenuList.length)
    );
  });

  const isAvailableMenu = computed(() => {
    return hasMenuContent.value || (props.menuInfo.path && hasNoChildren(props.menuInfo));
  });

  const computedDashboardMenu = computed(() => {
    if (!props.menuInfo.dashboardList) return null;
    return {
      ...props.menuInfo.dashboardList,
      subMenuList: props.dashboardMenuList,
    };
  });

  const computedAnalysisBoardMenu = computed(() => {
    if (!props.menuInfo.analysisBoardList) return null;
    return {
      ...props.menuInfo.analysisBoardList,
      subMenuList: props.analysisBoardMenuList,
    };
  });

  const onHoverButton = (e: MouseEvent) => {
    emit('hover', e);
  };

  // 클릭 이벤트 처리 (드래그와 구분)
  const clickStartTime = ref(0);
  const clickStartPosition = ref({ x: 0, y: 0 });
  const CLICK_THRESHOLD = 5; // 5px 이내 이동은 클릭으로 간주
  const LONG_PRESS_TIME = 300; // 300ms 이상 누르면 long press

  const onMouseDown = (e: MouseEvent) => {
    clickStartTime.value = Date.now();
    clickStartPosition.value = { x: e.clientX, y: e.clientY };
  };

  const onClickButton = (e: MouseEvent) => {
    const clickDuration = Date.now() - clickStartTime.value;
    const moveDistance = Math.sqrt(
      Math.pow(e.clientX - clickStartPosition.value.x, 2) +
        Math.pow(e.clientY - clickStartPosition.value.y, 2),
    );

    // long press이거나 이동 거리가 크면 드래그로 간주하여 클릭 무시
    if (clickDuration >= LONG_PRESS_TIME || moveDistance > CLICK_THRESHOLD) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    emit('click');
  };

  const calculateFoldedAreaHeight = (menuInfo: MenuInfo) => {
    const isOverMaxWidth = document.documentElement.clientWidth > 1600;

    // JS 상수 사용
    const MENU_ELEMENT_HEIGHT = MENU_HEIGHT.SUB_MENU_ELEMENT;
    const MID_MENU_TITLE_HEIGHT = MENU_HEIGHT.MID_MENU_TITLE;
    const MID_MENU_BAR_HEIGHT = isOverMaxWidth
      ? MENU_HEIGHT.MID_MENU_BAR_LARGE
      : MENU_HEIGHT.MID_MENU_BAR;
    const MARGIN_BOTTOM_HEIGHT = MENU_HEIGHT.MARGIN_BOTTOM;

    const midMenu = menuInfo.children?.filter((m) => m.children && m.children?.length);

    const numOfMidMenu = midMenu?.length ?? 0;
    const numOfElement =
      (menuInfo.children?.length ?? 0) +
      (midMenu?.reduce((acc, cur) => acc + (cur.children?.length ?? 0), 0) ?? 0);
    const numOfDashboardElement = menuInfo.dashboardList ? 1 : 0;
    const numOfAnalysisBoardElement = menuInfo.analysisBoardList ? 1 : 0;
    let numOfMidMenuBar =
      menuInfo.children?.length || menuInfo.dashboardList || menuInfo.analysisBoardList
        ? numOfMidMenu
        : numOfMidMenu - 1;
    if (numOfMidMenuBar < 0) {
      numOfMidMenuBar = 0;
    }
    const totalHeight =
      numOfMidMenu * MID_MENU_TITLE_HEIGHT +
      numOfMidMenuBar * MID_MENU_BAR_HEIGHT +
      (numOfElement + numOfDashboardElement + numOfAnalysisBoardElement) * MENU_ELEMENT_HEIGHT +
      (numOfMidMenu + numOfElement + numOfDashboardElement + numOfAnalysisBoardElement) *
        MARGIN_BOTTOM_HEIGHT;

    return totalHeight;
  };

  const controller = new AbortController();
  onBeforeMount(() => {
    innerHeight.value = `${calculateFoldedAreaHeight(props.menuInfo)}px`;
    window.addEventListener(
      'resize',
      () => {
        innerHeight.value = `${calculateFoldedAreaHeight(props.menuInfo)}px`;
      },
      { signal: controller.signal },
    );
  });

  // 26.1.21 기획 변경 - 구분선 제거
  // onMounted(() => {
  //   if (viewMode.value === VIEW_MODE.MAXGAUGE) {
  //     const el = '.menu-element__';
  //     const elements = document.body.querySelectorAll<HTMLLIElement>(
  //       el + GNB_DB_LISTS.join(`, ${el}`),
  //     );
  //     const lastElement = elements?.[elements.length - 1];

  //     if (lastElement?.className.includes(props.menuInfo.value)) {
  //       isMaxgaugeLastMenuElement.value = true;
  //     }
  //   }
  // });
  onUnmounted(() => {
    controller.abort();
  });

  return {
    innerRef,
    innerHeight,
    hasMenuContent,
    isAvailableMenu,

    mainPanelInfo,
    computedDashboardMenu,
    computedAnalysisBoardMenu,

    onMouseDown,
    onClickButton,
    onHoverButton,
  };
};
